import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth.hook";
import { Button, Input, GoogleButton } from "@/components/atoms";
import { FormField } from "@/components/molecules/FormField";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { SIGNIN_SEO } from "@/constants/site-seo";
import { applyPageMeta } from "@/utils/page-meta.utils";

export function SignInPage() {
  const { signIn, signInWithGoogle, resetPassword, loading } = useAuth();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);

  useEffect(() => {
    applyPageMeta({
      title: SIGNIN_SEO.title,
      description: SIGNIN_SEO.description,
      canonicalPath: "/signin",
      noIndex: true,
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await signIn(formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign in failed");
    }
  };

  const handleResetPassword = async () => {
    if (!formData.email) {
      setError("Please enter your email address first");
      return;
    }
    try {
      await resetPassword(formData.email);
      setResetEmailSent(true);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reset password failed");
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Google sign in failed");
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back — pick up your runway model where you left off."
      footer={
        <>
          Don&apos;t have an account?{" "}
          <Link to="/signup" className="font-semibold text-brand hover:text-brand-muted">
            Sign up
          </Link>
        </>
      }
    >
      <div className="sh-card p-6">
        {resetEmailSent ? (
          <div className="text-center">
            <div className="rounded-[14px] border border-brand/30 bg-brand/10 px-4 py-3 text-sm text-emerald-100">
              Password reset email sent. Check your inbox and follow the instructions.
            </div>
            <Button
              variant="secondary"
              size="md"
              className="mt-6 w-full"
              onClick={() => setResetEmailSent(false)}
            >
              Back to sign in
            </Button>
          </div>
        ) : (
          <form className="space-y-5" onSubmit={handleSubmit}>
            {error ? (
              <div className="rounded-[14px] border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
                {error}
              </div>
            ) : null}

            <FormField label="Email" required>
              <Input
                type="email"
                placeholder="you@startup.com"
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                required
              />
            </FormField>

            <FormField label="Password" required>
              <Input
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                required
              />
            </FormField>

            <div className="text-right text-sm">
              <button
                type="button"
                onClick={handleResetPassword}
                className="font-medium text-brand hover:text-brand-muted"
              >
                Forgot password?
              </button>
            </div>

            <Button variant="primary" size="lg" type="submit" loading={loading} className="w-full">
              Continue
            </Button>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-surface px-2 text-white/45">Or continue with</span>
              </div>
            </div>

            <GoogleButton onClick={handleGoogleSignIn} loading={loading}>
              Continue with Google
            </GoogleButton>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
